/**
 * Cash Transfers container (C23, B4 — D05 §1 – §5): binds `CashTransfers` (packages/ui, old CashTransfers.tsx) to the read
 * model `GET /transfers` (records, vault and currency balances from the ledger, the parties by id) and opens the
 * registration form (`transfers/FormContainer`), the details / voucher window (`transfers/ReceiptContainer`) and the
 * cancel dialog (D-086: reason → own PIN → one reversal entry; no permanent delete). The list filters (tab = route,
 * search over name / code / destination / partner, direction, currency, payment) are the old `filteredTransfers`
 * (:502-519). "مشاركة وتصدير" prints the filtered list as the A4 report of the old `handleShareClick` (:521-615) until the
 * PDF worker of B5 (as every "PDF" button since C19). Labels are formatted here; no money is computed (the report
 * summary adds decimal strings for display, as the statements do).
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PRINT_ROOT_CLASS, StatementA4, printDocument } from "@faylasuf/print";
import { CancelTransferDialog, CashTransfers, type CtBannerCard, type CtDirection, type CtPayment, type CtRow, type CtTab } from "@faylasuf/ui";
import type { TransferRowData, TransfersSummary } from "../api/types.js";
import { useApp, useSession } from "../app/context.js";
import { errorMessage, isSessionLost } from "../app/errors.js";
import { usePinGate } from "../app/pin-gate.js";
import { keys } from "../app/query.js";
import { useToast } from "../app/toast.js";
import { decAdd, isZero } from "./customers/format.js";
import { FormContainer } from "./transfers/FormContainer.js";
import { ReceiptContainer } from "./transfers/ReceiptContainer.js";
import { amountWith, currencyName, flagOf, money2, symbolOf } from "./transfers/format.js";
import { usePrintOffice } from "../app/printOffice.js";

export function useTransfers() {
  const { source } = useApp();
  const { sessionLost } = useSession();
  const q = useQuery({ queryKey: keys.transfers, queryFn: () => source.transfers(), staleTime: 10_000 });
  if (q.error && isSessionLost(q.error)) sessionLost();
  return q;
}

export function TransfersPage() {
  const { t, locale, source } = useApp();
  const { session, hasPermission, sessionLost } = useSession();
  const printOffice = usePrintOffice(); // [C29] F-286: identity lines in the A4 header
  const toast = useToast();
  const qc = useQueryClient();
  const pin = usePinGate();
  const q = useTransfers();
  const [tab, setTab] = useState<CtTab>("vault");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | CtDirection>("all");
  const [currencyFilter, setCurrencyFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState<"all" | CtPayment>("all");
  const [formOpen, setFormOpen] = useState(false);
  const [receipt, setReceipt] = useState<{ id: string; autoPrint: boolean } | null>(null);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reporting, setReporting] = useState(false);

  const data: TransfersSummary | undefined = q.data;
  const currencies = data?.currencies ?? [];

  const filtered: TransferRowData[] = useMemo(() => {
    if (!data) return [];
    const s = search.toLowerCase();
    return data.rows.filter((ct) => {
      const matchesSourceTab = (ct.route === "vault" ? "vault" : "office") === tab;
      const matchesSearch =
        ct.beneficiaryName.toLowerCase().includes(s) ||
        ct.code.toLowerCase().includes(s) ||
        ct.destination.toLowerCase().includes(s) ||
        (ct.partner !== null && ct.partner.name.toLowerCase().includes(s));
      const matchesType = typeFilter === "all" || (ct.direction === "out" ? "sent" : "received") === typeFilter;
      const matchesCurrency = currencyFilter === "all" || ct.currency === currencyFilter;
      const matchesPayment = paymentFilter === "all" || ct.paymentKind === paymentFilter;
      return matchesSourceTab && matchesSearch && matchesType && matchesCurrency && matchesPayment;
    });
  }, [data, search, tab, typeFilter, currencyFilter, paymentFilter]);

  const rows: CtRow[] = filtered.map((r) => ({
    id: r.id,
    numberLabel: `#${r.number}`,
    code: r.code,
    status: r.status,
    beneficiaryName: r.beneficiaryName,
    destination: r.destination,
    partnerName: r.partner?.name ?? null,
    customerName: r.customer ? r.customer.name : null,
    direction: r.direction === "out" ? "sent" : "received",
    payment: r.paymentKind,
    amountLabel: amountWith(r.amount, r.currency, currencies),
    commissionLabel: amountWith(r.commission, r.currency, currencies),
    partnerFeeLabel: amountWith(r.partnerFee, r.currency, currencies),
    totalLabel: amountWith(r.total, r.currency, currencies),
    settlementLabel: r.settlement ? `${money2(r.settlement.total)} ${r.settlement.currency === "IQD" ? "د.ع" : "$"}` : null,
  }));

  // hub banner (old :953-996): IQD vault, dollar vault (hidden with D-066), every visible currency of the office table
  const bannerCards: CtBannerCard[] = [];
  if (data) {
    bannerCards.push({ code: "IQD", flag: flagOf("IQD"), label: t("ct.stats_vault_iqd"), amountLabel: `${money2(data.banner.iqd?.balance ?? "0")} د.ع`, tone: "iqd" });
    if (!data.usdHidden) bannerCards.push({ code: "USD", flag: flagOf("USD"), label: t("ct.stats_vault_usd"), amountLabel: `${money2(data.banner.usd?.balance ?? "0")} $`, tone: "usd" });
    for (const c of data.currencies) bannerCards.push({ code: c.code, flag: flagOf(c.code), label: locale === "ar" ? c.nameAr : c.nameEn, amountLabel: `${money2(c.stock?.balance ?? "0")} ${c.symbol}`, tone: "other" });
  }
  const currencyOptions = [
    { id: "IQD", label: `${flagOf("IQD")} ${t("ct.cur_iqd")}`, subLabel: "د.ع" },
    ...(data?.usdHidden ? [] : [{ id: "USD", label: `${flagOf("USD")} ${t("ct.cur_usd")}`, subLabel: "$" }]),
    ...currencies.map((c) => ({ id: c.code, label: `${flagOf(c.code)} ${locale === "ar" ? c.nameAr : c.nameEn}`, subLabel: c.symbol })),
  ];

  // ---------------------------------------------------------------- cancel (D-086)
  const cancelRow = data?.rows.find((r) => r.id === cancelId) ?? null;
  const confirmCancel = async (reason: string) => {
    if (!cancelRow) return;
    setBusy(true);
    try {
      const res = await pin.withPin((proof) => source.cancelTransfer(cancelRow.id, reason, crypto.randomUUID(), proof.pin ?? ""), { message: t("ct.pin_message_cancel", { code: cancelRow.code }), allowSupervisor: false });
      if (!res) return;
      toast(t("ct.toast_cancelled"), "success");
      setCancelId(null);
      for (const k of [keys.transfers, keys.accounts, keys.dashboard, keys.currencies, keys.dollar]) void qc.invalidateQueries({ queryKey: k });
      void qc.invalidateQueries({ queryKey: ["customers"] });
      void qc.invalidateQueries({ queryKey: ["transfer-detail"] });
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      else toast(errorMessage(t, e), "error");
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- share (old handleShareClick → A4 report)
  useEffect(() => {
    if (!reporting) return;
    const done = () => setReporting(false);
    window.addEventListener("afterprint", done, { once: true });
    const h = setTimeout(() => printDocument("professional-print-template"), 80);
    return () => {
      clearTimeout(h);
      window.removeEventListener("afterprint", done);
    };
  }, [reporting]);
  let reportTitle = `${t("ct.report_title")} - ${tab === "vault" ? t("ct.report_vault") : t("ct.report_office")}`;
  if (search) reportTitle += ` - ${t("ct.report_search")} "${search}"`;
  else {
    const desc: string[] = [];
    if (typeFilter !== "all") desc.push(typeFilter === "sent" ? t("ct.report_sent") : t("ct.report_received"));
    if (currencyFilter !== "all") desc.push(currencyFilter);
    if (paymentFilter !== "all") desc.push(t(`ct.report_${paymentFilter}`));
    reportTitle += ` (${desc.length > 0 ? desc.join(" + ") : t("ct.report_all")})`;
  }
  const paymentWord = (r: TransferRowData) => (r.paymentKind === "cash" ? t("ct.report_cash") : r.paymentKind === "credit" ? t("ct.report_credit") : t("ct.report_trust_one"));
  const reportRows = filtered.map((item) => {
    let details = item.destination;
    if (item.partner) details += ` | ${t("ct.report_partner")} ${item.partner.name}`;
    if (item.customer) details += ` | ${item.paymentKind === "credit" ? t("ct.report_on_credit") : t("ct.report_on_trust")} ${item.customer.name}`;
    return {
      code: `#${item.number} - ${item.code}`,
      client: item.beneficiaryName,
      details,
      type: `${item.direction === "out" ? t("ct.sent") : t("ct.received")} (${paymentWord(item)}) - ${item.currency}`,
      amount: amountWith(item.amount, item.currency, currencies),
      commission: amountWith(item.commission, item.currency, currencies),
      ...(tab === "office" ? { partnerFee: amountWith(item.partnerFee, item.currency, currencies) } : {}),
      total: amountWith(item.total, item.currency, currencies),
    };
  });
  const reportSummary: { label: string; value: string }[] = [{ label: t("ct.report_count"), value: String(filtered.length) }];
  for (const code of ["IQD", "USD", ...currencies.map((c) => c.code)]) {
    const list = filtered.filter((x) => x.currency === code && x.status !== "cancelled");
    if (list.length === 0) continue;
    const totalAmt = list.reduce((acc, x) => decAdd(acc, x.amount), "0");
    const totalComm = list.reduce((acc, x) => decAdd(acc, x.commission), "0");
    const name = currencyName(code, currencies, t, locale);
    reportSummary.push({ label: t("ct.report_total_amount", { currency: name }), value: `${money2(totalAmt)} ${symbolOf(code, currencies)}` });
    if (!isZero(totalComm)) reportSummary.push({ label: t("ct.report_total_commission", { currency: name }), value: `${money2(totalComm)} ${symbolOf(code, currencies)}` });
  }
  const todayLabel = new Date().toLocaleDateString(locale === "ar" ? "ar-IQ" : "en-US", { timeZone: session.office.timeZone });

  return (
    <>
      <CashTransfers
        tab={tab}
        onTabChange={setTab}
        bannerCards={bannerCards}
        rows={rows}
        search={search}
        onSearchChange={setSearch}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        currencyFilter={currencyFilter}
        onCurrencyFilterChange={setCurrencyFilter}
        currencyOptions={currencyOptions}
        paymentFilter={paymentFilter}
        onPaymentFilterChange={setPaymentFilter}
        onShare={() => setReporting(true)}
        onAdd={() => setFormOpen(true)}
        onDetails={(id) => setReceipt({ id, autoPrint: false })}
        canCancel={hasPermission("delete_movements")}
        onCancel={(id) => setCancelId(id)}
      />
      {data && (
        <FormContainer
          open={formOpen}
          data={data}
          tab={tab}
          onTabChange={setTab}
          onClose={() => setFormOpen(false)}
          onPrint={(id) => setReceipt({ id, autoPrint: true })}
        />
      )}
      {receipt && data && <ReceiptContainer id={receipt.id} data={data} autoPrint={receipt.autoPrint} onClose={() => setReceipt(null)} />}
      <CancelTransferDialog open={cancelRow !== null} code={cancelRow?.code ?? ""} busy={busy} onConfirm={(r) => void confirmCancel(r)} onClose={() => setCancelId(null)} />
      <div className="relative z-[130]">{pin.dialog}</div>
      {reporting &&
        createPortal(
          <div className={PRINT_ROOT_CLASS} data-testid="print-root">
            <StatementA4
              locale={locale}
              title={reportTitle}
              {...(search ? { customerName: t("ct.report_search_customer", { q: search }) } : {})}
              dateLabel={todayLabel}
              docNumber={String(filtered.length)}
              office={printOffice}
              columns={[
                { header: t("ct.report_col_code"), key: "code" },
                { header: t("ct.report_col_client"), key: "client" },
                { header: t("ct.report_col_details"), key: "details" },
                { header: t("ct.report_col_type"), key: "type" },
                { header: t("ct.report_col_amount"), key: "amount", numeric: true },
                { header: t("ct.report_col_commission"), key: "commission", numeric: true },
                ...(tab === "office" ? [{ header: t("ct.report_col_fee"), key: "partnerFee", numeric: true }] : []),
                { header: t("ct.report_col_total"), key: "total", numeric: true },
              ]}
              rows={reportRows}
              summary={reportSummary}
            />
          </div>,
          document.body,
        )}
    </>
  );
}
